/**
 * @description Pre-logout flush registry. A page with unsaved work registers a
 * task here; `useAuth().logout` runs every task while the auth tokens are still
 * present, so an idle timeout persists the work before the session is torn
 * down. Kept outside React so any hook or interceptor path can drive it.
 *
 * @author [Rajkumar Rathod]
 */

export interface LogoutTaskContext {
  /** the logout was triggered by the idle timeout, not a deliberate sign-out */
  isIdle: boolean;
}

export type LogoutTask = (context: LogoutTaskContext) => Promise<void> | void;

const tasks = new Set<LogoutTask>();

export const registerLogoutTask = (task: LogoutTask) => {
  tasks.add(task);
  return () => {
    tasks.delete(task);
  };
};

/**
 * Runs every registered task and waits for all to settle so a save started here
 * lands before the caller clears the tokens. Failures are swallowed — logout
 * must never be held back by a rejected flush.
 */
export const runLogoutTasks = async (context: LogoutTaskContext) => {
  const pending = Array.from(tasks, (task) => Promise.resolve(task(context)));
  await Promise.allSettled(pending);
};
